/**
 * Impersonation Store
 * 
 * Manages user impersonation for demo/testing purposes.
 * Only available to SYSTEM_ADMIN and ACCOUNT_ADMIN in non-production environments.
 * 
 * When impersonating:
 * - All permission checks use the impersonated user's context
 * - The real admin context is preserved for security checks (opening the switcher)
 * - Audit entries are created for start/stop impersonation
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { type UserWorkContext } from '@entities/user';

// Feature flag - enable impersonation (demo/dev mode)
// In production, this would be controlled by environment variable
const IMPERSONATION_ENABLED = true; // import.meta.env.VITE_DEMO_IMPERSONATION_ENABLED === 'true';

export interface ImpersonationAuditEntry {
  id: string;
  timestamp: Date;
  action: 'START' | 'STOP';
  admin_user_id: string;
  admin_user_name?: string;
  acting_as_user_id: string;
  acting_as_user_name?: string;
  account_id: string;
}

interface ImpersonationState {
  // The user context we're impersonating (null if not impersonating)
  impersonatedContext: UserWorkContext | null;
  
  // The real admin's context (preserved for security checks)
  realAdminContext: UserWorkContext | null;
  
  // User display info for the banner
  impersonatedUserName: string | null;
  impersonatedUserRole: string | null;
  
  // Audit log
  auditLog: ImpersonationAuditEntry[];
  
  // Is the impersonation modal open?
  isModalOpen: boolean;
  
  // Actions
  startImpersonation: (params: {
    targetContext: UserWorkContext;
    targetUserName: string;
    targetUserRole: string;
    realAdminContext: UserWorkContext;
    realAdminName?: string;
  }) => void;
  
  stopImpersonation: () => void;
  
  // Modal controls
  openModal: () => void;
  closeModal: () => void;
  
  // Getters
  isImpersonating: () => boolean;
  getEffectiveContext: (realContext: UserWorkContext | null) => UserWorkContext | null;
  getRealAdminContext: () => UserWorkContext | null;
  
  // Feature check
  isImpersonationEnabled: () => boolean;
  canImpersonate: (systemRole: string) => boolean;
  
  // Audit
  getAuditLog: () => ImpersonationAuditEntry[];
  clearAuditLog: () => void;
}

export const useImpersonationStore = create<ImpersonationState>()(
  persist(
    (set, get) => ({
      impersonatedContext: null,
      realAdminContext: null,
      impersonatedUserName: null,
      impersonatedUserRole: null,
      auditLog: [],
      isModalOpen: false,
      
      startImpersonation: ({ targetContext, targetUserName, targetUserRole, realAdminContext, realAdminName }) => {
        const auditEntry: ImpersonationAuditEntry = {
          id: `audit-${Date.now()}`,
          timestamp: new Date(),
          action: 'START',
          admin_user_id: realAdminContext.id,
          admin_user_name: realAdminName,
          acting_as_user_id: targetContext.id,
          acting_as_user_name: targetUserName,
          account_id: realAdminContext.account_id,
        };
        
        set((state) => ({
          impersonatedContext: targetContext,
          realAdminContext: realAdminContext,
          impersonatedUserName: targetUserName,
          impersonatedUserRole: targetUserRole,
          auditLog: [auditEntry, ...state.auditLog].slice(0, 100), // Keep last 100 entries
          isModalOpen: false,
        }));
        
        console.log('[IMPERSONATION] Started impersonating:', targetUserName, 'as', targetUserRole);
      },
      
      stopImpersonation: () => {
        const state = get();
        if (state.realAdminContext && state.impersonatedContext) {
          const auditEntry: ImpersonationAuditEntry = {
            id: `audit-${Date.now()}`,
            timestamp: new Date(),
            action: 'STOP',
            admin_user_id: state.realAdminContext.id,
            admin_user_name: undefined,
            acting_as_user_id: state.impersonatedContext.id,
            acting_as_user_name: state.impersonatedUserName || undefined,
            account_id: state.realAdminContext.account_id,
          };
          
          set((prevState) => ({
            impersonatedContext: null,
            realAdminContext: null,
            impersonatedUserName: null,
            impersonatedUserRole: null,
            auditLog: [auditEntry, ...prevState.auditLog].slice(0, 100),
          }));
          
          console.log('[IMPERSONATION] Stopped impersonation');
        } else {
          set({
            impersonatedContext: null,
            realAdminContext: null,
            impersonatedUserName: null,
            impersonatedUserRole: null,
          });
        }
      },
      
      openModal: () => set({ isModalOpen: true }),
      closeModal: () => set({ isModalOpen: false }),
      
      isImpersonating: () => get().impersonatedContext !== null,
      
      getEffectiveContext: (realContext) => {
        const { impersonatedContext } = get();
        // Return impersonated context if active, otherwise the real context
        return impersonatedContext || realContext;
      },
      
      getRealAdminContext: () => get().realAdminContext,
      
      isImpersonationEnabled: () => IMPERSONATION_ENABLED,
      
      canImpersonate: (systemRole) => {
        if (!IMPERSONATION_ENABLED) return false;
        return systemRole === 'ACCOUNT_ADMIN';
      },
      
      getAuditLog: () => get().auditLog,
      
      clearAuditLog: () => set({ auditLog: [] }),
    }),
    {
      name: 'impersonation-storage',
      partialize: (state) => ({
        // Persist impersonation state across page refreshes
        impersonatedContext: state.impersonatedContext,
        realAdminContext: state.realAdminContext,
        impersonatedUserName: state.impersonatedUserName,
        impersonatedUserRole: state.impersonatedUserRole,
        auditLog: state.auditLog,
      }),
    }
  )
);
